import React, { memo, useCallback, useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import Link from 'next/link';

import interactionResponse from 'await-interaction-response';
import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Pagination, Select } from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { viewNationwideInventoryClick } from 'reaxl-analytics-handlers';

import getPathSRP from '@/utilities/getPathSRP';

import { srpNumRecordsDropDownClick, srpPaginationClick } from '@/analytics/srpAnalyticsHandlers';

import queryDuck from '@/ducks/queryDuck';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';
import srpPaginationDuck from '@/ducks/srp/srpPaginationDuck.js';
import srpResultsDuck from '@/ducks/srp/srpResultsDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import SearchResultsPageNumContainer from './SearchResultsPageNumContainer';

function InventoryPaginationContainer() {
    const device = useDevice();
    const {
        default_num_records: [, { sortOptions: numRecordsSortOptions }],
        srp_infinite_scroll: [, {
            button: isButtonScroll,
        }],
        srp_nationwide_pagination_link: [isSRPNationwidePaginationLinkEnabled],
    } = useFeatures([
        'default_num_records',
        'srp_infinite_scroll',
        'srp_nationwide_pagination_link',
    ]);
    const notXS = _get(device, 'greaterThan.xs', false);
    const navigateToSrp = useSrpNavigation();
    const currentPage = useSelector(srpPaginationDuck.selectors.getCurrentPage);
    const numRecords = useSelector(srpPaginationDuck.selectors.getNumRecords);
    const searchRadius = useSelector(srpFiltersDuck.selectors.getSelectedSearchRadius);
    const count = useSelector(srpResultsDuck.selectors.getResultsCount);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const maxResultCount = useSelector(srpResultsDuck.selectors.getMaxActiveResultsCount);
    const resultCount = useSelector(srpResultsDuck.selectors.getResultsCount);

    const totalPages = Math.ceil(Math.min(count, maxResultCount) / numRecords);
    const numRecordsData = { ...numRecordsSortOptions, value: numRecords };

    const [nationwideLink, setNationwideLink] = useState('');

    const handlePageSelect = async (pageNumber, event) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;
        sendClick(srpPaginationClick, event, {
            pageNumber,
        });

        // push new instance of srp to history
        navigateToSrp({ isNewSearch: false, filtersValues: { firstRecord: (pageNumber - 1) * numRecords } });
    };

    const handleNumRecordsChange = async (event) => {
        const { value } = event.target;
        await interactionResponse();
        event.target.value = value;
        const recordAmount = event.target.value;
        sendClick(srpNumRecordsDropDownClick, event, {
            recordAmount,
        });

        // push new instance of srp to history
        navigateToSrp({ isNewSearch: false, filtersValues: { numRecords: recordAmount } });
    };

    const renderSelectionBox = () => searchRadius !== 0 && (
        <Select
            className="margin-0"
            selectClassName="margin-right-4"
            data-cmp="sel-numRecords"
            onChange={handleNumRecordsChange}
            aria-label="Show Number of Listings"
            {...numRecordsData}
        />
    );
    const handleNationwideClick = async (event) => {
        await interactionResponse();
        sendClick(viewNationwideInventoryClick, event);
    };
    const getNationwideLink = useCallback(async () => {
        const nationwideQuery = { ...query };
        delete nationwideQuery.zip;
        delete nationwideQuery.city;
        delete nationwideQuery.state;
        const path = await getPathSRP({ ...nationwideQuery, searchRadius: '0' }, { basePath: false });
        setNationwideLink(path);
    }, [query]);

    useEffect(() => {
        getNationwideLink();
    }, [getNationwideLink]);

    const itemsPerPageMax = (numRecordsData?.value) || 25;

    const getNumbThru = () => {
        let numthru = Math.abs(Number.isInteger(currentPage) ? currentPage : 1) * itemsPerPageMax || itemsPerPageMax;

        if (numthru >= resultCount || numthru < itemsPerPageMax) {
            numthru = resultCount;
        }
        return numthru;
    };

    if (isButtonScroll && maxResultCount && getNumbThru() >= maxResultCount) {
        return (

            <div
                className="text-center margin-top-5 margin-bottom-5"
                data-cmp="max-results-message"
            >
                You have reached the maximum results allowed for a search. Please update your search criteria.
                {isSRPNationwidePaginationLinkEnabled
                    && (
                        <div>
                            <Link
                                href={nationwideLink}
                                onClick={handleNationwideClick}

                            >
                                View Nationwide Inventory
                            </Link>
                        </div>
                    )}

            </div>
        );
    }

    return !isButtonScroll && (
        <>
            <div
                className="display-flex justify-content-center align-items-center flex-wrap flex-column"
            >
                <SearchResultsPageNumContainer />
                {isSRPNationwidePaginationLinkEnabled
                    && (
                        <Link
                            href={nationwideLink}
                            onClick={handleNationwideClick}
                        >
                            View Nationwide Inventory
                        </Link>
                    )}
            </div>
            <div
                className="display-flex justify-content-center align-items-center flex-column flex-sm-row flex-sm-wrap margin-bottom-5 margin-top-4"
                data-cmp="cntnr-pagination"
            >
                {totalPages > 1 && (
                    <Pagination
                        activePage={currentPage}
                        items={totalPages}
                        onSelect={handlePageSelect}
                        maxButtons={notXS ? 13 : 7}
                        ellipsis={false}
                        version="v2"
                        id="srp-pagination"
                        className="margin-top-5 margin-horizontal-sm-5"
                    />
                )}
                {renderSelectionBox()}
            </div>
        </>
    );
}

export default memo(InventoryPaginationContainer);
